/**
 * Polygraf Protocol Types
 *
 * Defines the data structures for the Polygraf v2 protocol
 * for real-time speech-to-text transcription and presentation image streaming.
 */
import { z } from 'zod';
import { ScreenFormat } from './types.js';
// ============================================================================
// Protocol Messages
// ============================================================================
/**
 * Message types for Polygraf v2 protocol
 */
export var MessageType;
(function (MessageType) {
    // Client -> Server
    MessageType["SUBSCRIBE"] = "subscribe";
    MessageType["UNSUBSCRIBE"] = "unsubscribe";
    // Client -> Server: optional self-reported viewer identity for the presenter
    // roster (MP-194). A logged-in transcript viewer sends its display name so
    // the transcriber sees WHO is watching, not just a count; anonymous viewers
    // never send it and stay unnamed. Presenter-facing presence telemetry only —
    // NOT an auth/authorization signal (the name is client-asserted), so it must
    // never gate access to anything.
    MessageType["VIEWER_IDENTIFY"] = "viewer_identify";
    // Client -> Server: in-band session token for sockets that cannot carry the
    // Better Auth cookie (the native apps' WebView viewers). Sent BEFORE
    // `subscribe`; the server then evaluates the subscribe gate as that user.
    // Replaces a `?token=` upgrade query, which Caddy/Vector would log in full.
    MessageType["AUTHENTICATE"] = "authenticate";
    // Server -> Client: answer to `authenticate` (`ok` = the connection now has
    // a user identity). Informational — never retry on `ok: false`.
    MessageType["AUTHENTICATED"] = "authenticated";
    // Server -> Client
    MessageType["DOCUMENT_STATE"] = "document_state";
    MessageType["DOCUMENT_UPDATE"] = "document_update";
    // Bidirectional
    MessageType["PING"] = "ping";
    MessageType["PONG"] = "pong";
    MessageType["ERROR"] = "error";
    // mDNS Discovery
    MessageType["MDNS_SUBSCRIBE"] = "mdns_subscribe";
    MessageType["MDNS_UNSUBSCRIBE"] = "mdns_unsubscribe";
    MessageType["MDNS_STATE"] = "mdns_state";
    MessageType["MDNS_SOURCE_DISCOVERED"] = "mdns_source_discovered";
    MessageType["MDNS_SOURCE_UPDATED"] = "mdns_source_updated";
    MessageType["MDNS_SOURCE_REMOVED"] = "mdns_source_removed";
    // Real-time streaming (character-level)
    MessageType["STREAMING_TEXT"] = "streaming_text";
    // Server -> writer: current subscriber (viewer) count for the writer's document.
    // Emitted when the count changes (subscribe / unsubscribe / disconnect) so the
    // writer side can decide whether to keep publishing or pause.
    MessageType["SUBSCRIBER_COUNT"] = "subscriber_count";
    // File sharing
    MessageType["FILE_UPLOADED"] = "file_uploaded";
    MessageType["FILE_REMOVED"] = "file_removed";
    // Live session announcements (session list realtime feed)
    MessageType["SESSION_SUBSCRIBE"] = "session_subscribe";
    MessageType["SESSION_UNSUBSCRIBE"] = "session_unsubscribe";
    MessageType["SESSION_ANNOUNCED"] = "session_announced";
    MessageType["SESSION_ENDED"] = "session_ended";
})(MessageType || (MessageType = {}));
/**
 * Error `code` carried by a {@link MessageType.ERROR} message when a
 * `subscribe` request is refused by the space/room ACL (MP-215, UAT B5).
 *
 * The denial is in-band: the server keeps the socket OPEN (closing it would
 * trigger the client's auto-reconnect backoff and hammer the server) and the
 * client must treat it as a terminal, non-retryable state — render an
 * access-denied screen instead of scheduling a retry.
 */
export const WS_ERROR_ACCESS_DENIED = 'ACCESS_DENIED';
/**
 * Error code sent when the server could not EVALUATE a subscribe's access
 * (the space/room ACL lookup itself failed, e.g. a database outage). Unlike
 * `ACCESS_DENIED` this is transient: the subscription is refused fail-closed,
 * but the client may retry.
 */
export const WS_ERROR_AUTHORIZATION_FAILED = 'AUTHORIZATION_FAILED';
// ============================================================================
// Live Session Roles & Status
// ============================================================================
/**
 * Lifecycle status for a live session.
 * Mirrors the Prisma `SessionStatus` enum (must use the same string values).
 */
export var SessionStatus;
(function (SessionStatus) {
    SessionStatus["ACTIVE"] = "ACTIVE";
    SessionStatus["ENDED"] = "ENDED";
})(SessionStatus || (SessionStatus = {}));
/**
 * Role a participant occupies inside a live session. Surfaced through the
 * Phase-1 `sessions.*` tRPC routes; on the server the legacy
 * `ParticipantRole.BROADCASTER` row is used to back `PRESENTER`.
 */
export var SessionRole;
(function (SessionRole) {
    SessionRole["WRITER"] = "WRITER";
    SessionRole["PRESENTER"] = "PRESENTER";
})(SessionRole || (SessionRole = {}));
/**
 * Document update operations
 */
export var UpdateOperation;
(function (UpdateOperation) {
    UpdateOperation["INSERT"] = "insert";
    UpdateOperation["DELETE"] = "delete";
    UpdateOperation["REPLACE"] = "replace";
    UpdateOperation["FORMAT"] = "format";
})(UpdateOperation || (UpdateOperation = {}));
// ============================================================================
// Source Types
// ============================================================================
/**
 * Types of sources available in the system
 * Note: Values must match Prisma SourceType enum (uppercase)
 *
 * `SCREENCAST` is LEGACY. It once meant "the server captures this endpoint on a
 * timer"; that capture service is retired and nothing creates SCREENCAST any
 * more. The rows are still in the database, so every READ path must keep
 * treating them as presented-image sources — use {@link isScreenLikeSource}
 * rather than comparing to `SourceType.SCREEN` directly.
 */
export var SourceType;
(function (SourceType) {
    SourceType["TRANSCRIPT"] = "TRANSCRIPT";
    SourceType["SCREEN"] = "SCREEN";
    SourceType["SCREENCAST"] = "SCREENCAST";
})(SourceType || (SourceType = {}));
/**
 * True for any source that renders as a presentation image — i.e. SCREEN and
 * its retired predecessor SCREENCAST.
 *
 * Exists because `type === SourceType.SCREEN` is subtly wrong on every read
 * path: it silently drops the legacy SCREENCAST rows still live in MU's
 * database, so an existing classroom source vanishes from a source picker or
 * fails to route to a viewer — with nothing logged, because "no match" is not
 * an error. Some call sites spelled out `=== SCREEN || === SCREENCAST` and
 * others didn't, which is exactly the drift this collapses.
 *
 * Takes a plain string so callers holding an unnarrowed tRPC/DB value (`type:
 * string`) don't have to cast.
 */
export function isScreenLikeSource(type) {
    return type === SourceType.SCREEN || type === SourceType.SCREENCAST;
}
/**
 * Discovery method for sources
 */
export var DiscoveryMethod;
(function (DiscoveryMethod) {
    DiscoveryMethod["CLOUD"] = "cloud";
    DiscoveryMethod["MDNS"] = "mdns";
    DiscoveryMethod["UDP_BROADCAST"] = "udp_broadcast";
    DiscoveryMethod["STATIC_LIST"] = "static_list";
    DiscoveryMethod["QR_CODE"] = "qr_code";
    DiscoveryMethod["MANUAL"] = "manual";
})(DiscoveryMethod || (DiscoveryMethod = {}));
// ============================================================================
// Space & Room Types
// ============================================================================
/**
 * Visibility setting for spaces and rooms
 */
export var Visibility;
(function (Visibility) {
    Visibility["PUBLIC"] = "PUBLIC";
    Visibility["IP_RESTRICTED"] = "IP_RESTRICTED";
    Visibility["AUTHENTICATED"] = "AUTHENTICATED";
    Visibility["PRIVATE"] = "PRIVATE";
})(Visibility || (Visibility = {}));
/**
 * mDNS service type for Polygraf server discovery
 */
export const MDNS_SERVER_SERVICE_TYPE = 'polygraf';
/**
 * mDNS service types for source discovery.
 * Includes legacy Writer app service type (_wws._tcp).
 */
export const MDNS_SOURCE_SERVICE_TYPES = {
    TRANSCRIPT: 'polygraf-transcript',
    SCREEN: 'polygraf-screen',
    /** Legacy Writer app announces via _wws._tcp (WebSocket Secure) */
    WWS: 'wws',
};
/**
 * Known TXT record keys for Polygraf server mDNS announcements
 */
export const MDNS_TXT_KEYS = {
    VERSION: 'version',
    NAME: 'name',
    PROTOCOL: 'protocol',
};
// ============================================================================
// UDP Broadcast Discovery
// ============================================================================
/** Port for UDP broadcast discovery packets */
export const UDP_BROADCAST_PORT = 5557;
/** Size of the UDP discovery packet in bytes */
export const UDP_BROADCAST_PACKET_SIZE = 45;
/** Magic bytes at the start of the discovery packet */
export const UDP_BROADCAST_MAGIC_BYTES = [0x00, 0x03, 0xb3, 0x44];
/** Prefix in UDP response indicating server name */
export const UDP_BROADCAST_RESPONSE_PREFIX = 'Server-Name:';
/** Backoff configuration for UDP broadcast retries */
export const UDP_BROADCAST_BACKOFF = {
    INITIAL_MS: 300,
    MULTIPLIER: 1.5,
    MAX_MS: 15000,
};
/** How long before a UDP-discovered service is considered stale */
export const UDP_SERVICE_STALE_MS = 15000;
// ============================================================================
// Screen Source Constants
// ============================================================================
/** Default port for screen/presentation sources (VGA Broadcaster) */
export const DEFAULT_SCREEN_PORT = 1881;
/** Default image path for screen sources */
export const DEFAULT_SCREEN_IMAGE_PATH = '/vgabroadcaster.jpg';
/** Endpoint to resolve a device's friendly name */
export const DEVICE_NAME_ENDPOINT = '/u/devicename.txt';
/** Ports to probe when resolving device name */
export const DEVICE_NAME_PROBE_PORTS = [80, 1882];
// ============================================================================
// File Sharing
// ============================================================================
/** Maximum file upload size in bytes (100 MB) */
export const MAX_FILE_SIZE = 100 * 1024 * 1024;
// ============================================================================
// Zod Schemas for Validation
// ============================================================================
export const TextSegmentSchema = z.object({
    text: z.string(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    subscript: z.boolean().optional(),
    superscript: z.boolean().optional(),
    color: z.string().optional(),
    backgroundColor: z.string().optional(),
});
export const ParagraphSchema = z.object({
    id: z.string(),
    segments: z.array(TextSegmentSchema),
    timestamp: z.number().optional(),
    headingLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
});
export const DocumentStateSchema = z.object({
    id: z.string(),
    title: z.string(),
    paragraphs: z.array(ParagraphSchema),
    version: z.number(),
    lastModified: z.number(),
});
/**
 * Validates the server -> writer subscriber-count message. Used on the
 * writer side to reject malformed payloads instead of trusting the wire
 * `count: number` (which would silently accept Infinity / NaN / negative
 * values and drift the auto-pause UI).
 */
export const SubscriberCountMessageSchema = z.object({
    type: z.literal(MessageType.SUBSCRIBER_COUNT),
    documentId: z.string().min(1),
    count: z.number().int().nonnegative(),
    timestamp: z.number().int().nonnegative(),
});
/** Upper bound on an `authenticate` session token (Better Auth tokens are ~32 chars). */
export const AUTHENTICATE_TOKEN_MAX_LENGTH = 512;
/**
 * Validates the client -> server `authenticate` message. Strict: no extra keys,
 * a bounded non-empty token.
 */
export const AuthenticateMessageSchema = z
    .object({
    type: z.literal(MessageType.AUTHENTICATE),
    token: z.string().min(1).max(AUTHENTICATE_TOKEN_MAX_LENGTH),
})
    .strict();
export const RoomServiceSchema = z.object({
    id: z.string(),
    roomId: z.string(),
    type: z.nativeEnum(SourceType),
    name: z.string().min(1).max(100),
    format: z.nativeEnum(ScreenFormat).optional(),
    endpoint: z.string().optional(),
    isActive: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const SpaceSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    visibility: z.nativeEnum(Visibility),
    allowedIpRanges: z.array(z.string()).optional(),
    allowedDomains: z.array(z.string()).optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const RoomSchema = z.object({
    id: z.string(),
    spaceId: z.string(),
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    visibility: z.nativeEnum(Visibility),
    allowedIpRanges: z.array(z.string()).optional(),
    allowedDomains: z.array(z.string()).optional(),
    services: z.array(RoomServiceSchema),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
//# sourceMappingURL=protocol.js.map